import React, { useState, useEffect, useCallback } from 'react';
import { InventoryLocation } from '../../types/inventory';
import { inventoryAPI } from '../../services/inventoryApi';

const LOCATION_TYPE_ICONS: Record<string, string> = {
  building: '\u{1F3E2}',
  room: '\u{1F6AA}',
  freezer: '\u{2744}\u{FE0F}',
  fridge: '\u{2744}\u{FE0F}',
  shelf: '\u{1F4DA}',
  rack: '\u{1F5C4}\u{FE0F}',
  box: '\u{1F4E6}',
  well: '\u{1F7E2}',
};

const TEMP_COLORS: Record<string, string> = {
  ambient: '#6B7280',
  '4C': '#3B82F6',
  '-20C': '#6366F1',
  '-80C': '#8B5CF6',
  'LN2': '#EC4899',
};

function buildTree(locations: InventoryLocation[]): InventoryLocation[] {
  const map = new Map<number, InventoryLocation>();
  const roots: InventoryLocation[] = [];

  for (const loc of locations) {
    map.set(loc.location_id, { ...loc, children: [] });
  }

  for (const loc of locations) {
    const node = map.get(loc.location_id)!;
    if (loc.parent_id && map.has(loc.parent_id)) {
      map.get(loc.parent_id)!.children!.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

const LocationTree: React.FC = () => {
  const [locations, setLocations] = useState<InventoryLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('room');
  const [newParent, setNewParent] = useState<number | ''>('');
  const [newTemp, setNewTemp] = useState('');
  const [creating, setCreating] = useState(false);

  const loadLocations = useCallback(() => {
    setLoading(true);
    inventoryAPI.getLocationTree()
      .then(res => setLocations(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadLocations(); }, [loadLocations]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await inventoryAPI.createLocation({
        parent_id: newParent ? Number(newParent) : undefined,
        name: newName.trim(),
        location_type: newType,
        temperature_zone: newTemp || undefined,
      });
      setNewName('');
      setNewType('room');
      setNewParent('');
      setNewTemp('');
      setShowCreate(false);
      loadLocations();
    } catch {}
    setCreating(false);
  };

  const tree = buildTree(locations);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
          Storage Locations
        </h3>
        <button
          onClick={() => setShowCreate(!showCreate)}
          style={{
            padding: '6px 14px', borderRadius: 6, border: 'none',
            backgroundColor: '#0D9488', color: '#fff', fontSize: 12,
            fontWeight: 500, cursor: 'pointer',
          }}
        >
          {showCreate ? 'Cancel' : '+ Add Location'}
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} style={{
          marginBottom: 16, padding: 16,
          border: '1px solid var(--card-border)', borderRadius: 8,
          backgroundColor: 'var(--card-bg)',
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Name *</label>
              <input value={newName} onChange={e => setNewName(e.target.value)}
                style={inputStyle} placeholder="e.g. Freezer A" />
            </div>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={newType} onChange={e => setNewType(e.target.value)} style={inputStyle}>
                {['building', 'room', 'freezer', 'fridge', 'shelf', 'rack', 'box', 'well'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Parent</label>
              <select value={newParent} onChange={e => setNewParent(e.target.value ? Number(e.target.value) : '')} style={inputStyle}>
                <option value="">None (top-level)</option>
                {locations.map(l => (
                  <option key={l.location_id} value={l.location_id}>
                    {l.materialised_path && l.materialised_path !== '/' ? '  '.repeat(Math.max(0, (l.materialised_path.match(/\//g) || []).length - 1)) : ''}
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Temperature</label>
              <select value={newTemp} onChange={e => setNewTemp(e.target.value)} style={inputStyle}>
                <option value="">Not set</option>
                {['ambient', '4C', '-20C', '-80C', 'LN2'].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" disabled={creating || !newName.trim()} style={{
            marginTop: 12, padding: '8px 20px', borderRadius: 6,
            border: 'none', backgroundColor: '#0D9488', color: '#fff',
            fontWeight: 500, fontSize: 13, cursor: 'pointer',
          }}>
            {creating ? 'Creating...' : 'Create Location'}
          </button>
        </form>
      )}

      {/* Tree */}
      {loading ? (
        <div style={{ color: 'var(--text-secondary)', padding: 20 }}>Loading locations...</div>
      ) : tree.length === 0 ? (
        <div style={{
          padding: 40, textAlign: 'center', color: 'var(--text-secondary)',
          border: '1px dashed var(--card-border)', borderRadius: 8,
        }}>
          No locations defined. Click "+ Add Location" to create your first storage location.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--card-border)', borderRadius: 8, overflow: 'hidden' }}>
          {tree.map(node => (
            <LocationNode key={node.location_id} node={node} depth={0} />
          ))}
        </div>
      )}
    </div>
  );
};

const LocationNode: React.FC<{ node: InventoryLocation; depth: number }> = ({ node, depth }) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <>
      <div
        onClick={() => hasChildren && setExpanded(!expanded)}
        style={{
          padding: '10px 16px',
          paddingLeft: 16 + depth * 24,
          borderBottom: '1px solid var(--card-border)',
          cursor: hasChildren ? 'pointer' : 'default',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        {hasChildren && (
          <span style={{ fontSize: 10, color: 'var(--text-secondary)', minWidth: 12 }}>
            {expanded ? '\u25BC' : '\u25B6'}
          </span>
        )}
        {!hasChildren && <span style={{ minWidth: 12 }} />}

        <span style={{ fontSize: 14 }}>
          {LOCATION_TYPE_ICONS[node.location_type] || '\u{1F4C1}'}
        </span>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
          {node.name}
        </span>
        <span style={{
          fontSize: 11, color: 'var(--text-secondary)',
          padding: '1px 6px', borderRadius: 4,
          backgroundColor: 'rgba(107,114,128,0.1)',
        }}>
          {node.location_type}
        </span>
        {node.temperature_zone && (
          <span style={{
            fontSize: 10, fontWeight: 500,
            color: TEMP_COLORS[node.temperature_zone] || '#6B7280',
            padding: '1px 6px', borderRadius: 4,
            backgroundColor: `${TEMP_COLORS[node.temperature_zone] || '#6B7280'}15`,
          }}>
            {node.temperature_zone}
          </span>
        )}
        {node.sample_count > 0 && (
          <span style={{ fontSize: 11, color: '#0D9488', marginLeft: 'auto' }}>
            {node.sample_count} samples
          </span>
        )}
      </div>
      {expanded && node.children && node.children.map(child => (
        <LocationNode key={child.location_id} node={child} depth={depth + 1} />
      ))}
    </>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 500,
  color: 'var(--text-secondary)', marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6,
  border: '1px solid var(--card-border)',
  backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)',
  fontSize: 13, outline: 'none', boxSizing: 'border-box' as any,
};

export default LocationTree;
